import type { ComponentType } from "react"
import { useCallback, useContext, useMemo } from "react"
import { ModalContext } from "./ModalContext"
import type { ModalProps } from "./ModalProvider"

/**
 * Callback types provided for descriptive type-hints
 */
type ShowModal<Props = void> = (props: Props) => void

/**
 * Utility function to generate unique number per component instance
 */
const generateModalKey = (() => {
	let count = 0

	return () => `${++count}`
})()

/**
 * React hook for showing modal windows
 */
export const useModal = <Props>(component: ComponentType<ModalProps<Props>>): ShowModal<Props> => {
	const { showModal } = useContext(ModalContext)

	const key = useMemo(() => generateModalKey(), [])

	return useCallback((props?: Props) => showModal(key, component, props), [component, showModal, key])
}
